import React from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Paragraph,
  Block,
  DynamicMediaItem,
} from '@uncinc/react-drupal-core';
import {
  Container,
  WysiwygField,
  useWindowDimensions,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  ImmutableEntity,
  ImmutableRootState,
} from '@uncinc/react-drupal-core/types';

import {
  getOverviewUrlFromMenu,
} from '../../../../helpers';

import { NodeArticle } from '../../../../types/uncinc_profile/nodes';
import BackButton from '../../../../components/BackButton';
import ShareWrapper from '../../../../components/ShareWrapper';
import ArticleMetadata from '../../../../components/ArticleMetadata';
import ThemesLabels from '../../../../components/ThemesLabels';
import {
  SCREEN_TABLET, BLOCK_RELATED_ARTICLES_BLOCK_ID,
} from '../../../../config/constants';

import './style.scss';


interface NodeArticlePageProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeArticle>;
}

const NodeArticlePage: React.FC<NodeArticlePageProps> = ({ entity }) => {
  const { t } = useTranslation();
  const classes = classNames('NodeArticlePage');
  const article = entity.toJS();
  const title = article.title[0].value;
  const articleType = article.field_article_type[0].entity.name[0].value;
  const articleTypeTargetId = article.field_article_type[0]?.target_id;
  const imageCaption = article.field_media_item[0]?.entity.field_caption[0]?.value;
  const paragraphs = article.field_paragraphs;
  const labels = article.field_labels;
  const labelsIds = labels.map((label) => label.target_id);
  const body = article.body[0]?.processed;
  const nid = article.nid[0]?.value;
  const publicationDate = article.field_publish_date[0]?.value || '';
  const themes = article.field_themes;
  const readTime = Number(article.node_read_time[0]?.value);
  const themeIds = themes.map((label) => label.target_id);
  const imageStyles = article.field_media_item[0];

  const menu = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['menu', 'main']));
  const overviewUrl = getOverviewUrlFromMenu(menu, 'articles');

  // Set the block filters.
  const blockFilters: any = { field_article_type_target_id: [articleTypeTargetId] };
  if (themeIds.length > 0) blockFilters.field_themes_target_id = themeIds;
  if (labelsIds.length > 0) blockFilters.field_labels_target_id = labelsIds;

  const { width } = useWindowDimensions();
  const isMobile = width <= SCREEN_TABLET;

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>

      <div className="NodeArticlePage__hero">
        <Container>
          <BackButton
            text={t('content_types.article.overview')}
            to={overviewUrl}
          />
        </Container>

        <Container>
          <div className="NodeArticlePage__hero__header col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">

            <ArticleMetadata>
              <span>
                {articleType ? (
                  <>{articleType}</>
                ) : (
                  <>{t('content_types.article.type')}</>
                )}
              </span>
              <span className="dot">&bull;</span>
              <ArticleMetadata.PublicationDate publicationDate={new Date(publicationDate)} />
              <span className="dot">&bull;</span>
              <ArticleMetadata.ReadTime readTime={readTime} />
            </ArticleMetadata>

            <h1 itemProp="name" className="NodeArticlePage__hero__title">{title}</h1>

            {themes.length > 0 && (
              <ThemesLabels themes={themes} />
            )}

            {body && (
              <WysiwygField
                property="schema:text"
                html={body}
              />
            )}

          </div>

        </Container>

        <Container>
          <div className="row">
            <div className="col-12">
              {imageStyles && (
                <>
                  <div className="NodeArticlePage__hero__image">
                    <DynamicMediaItem
                      media={imageStyles}
                      imageAspectRatio={
                        isMobile ? '4_3' : '16_9'
                      }
                    />
                  </div>
                  {imageCaption && (
                    <p className="NodeArticlePage__hero__image__caption">{imageCaption}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </Container>
      </div>

      {
        paragraphs.length !== 0 ? (
          <div className="NodeArticlePage__paragraphs">
            {paragraphs.map((paragraph) => {
              const id = paragraph.target_id;
              const language = paragraph.entity.langcode[0].value;
              return (
                <Paragraph
                  key={`paragraph:${id}:${language}`}
                  entity={Immutable.fromJS(paragraph.entity)}
                />
              );
            })}
          </div>
        ) : null
      }

      { /* Social Share Block */}
      <ShareWrapper withThemes themes={themes} />

      {/* Related articles */}
      <Block
        id={BLOCK_RELATED_ARTICLES_BLOCK_ID}
        link={overviewUrl}
        linkTitle={t('content_types.article.overview')}
        viewId="article"
        viewDisplay="related"
        viewFilters={blockFilters}
        viewArgs={[nid]}
      />

    </article>
  );
};

export default NodeArticlePage;
